/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Add hover effect to product thumbnails to change the main image.
 * Improved to prevent automatic scrolling/jumping when hovering.
 */
document.addEventListener('mouseover', (event) => {
    // We look for any element with data-action="select" (thumbnails)
    const thumbnail = event.target.closest('[data-action="select"]');

    // We only proceed if it's inside a product-media element and it's not already selected
    if (thumbnail && thumbnail.closest('product-media') && thumbnail.getAttribute('aria-current') !== 'true') {
        const flickityControls = thumbnail.closest('flickity-controls');

        if (flickityControls && typeof flickityControls.flickityCarousel !== 'undefined') {
            const controlsId = thumbnail.getAttribute('aria-controls');
            if (controlsId) {
                // Find the scrollable container (the thumbnail list)
                const scrollContainer = thumbnail.closest('.product__thumbnail-list');

                if (scrollContainer) {
                    // Temporarily disable scrollTo to prevent the theme's automatic centering
                    const originalScrollTo = scrollContainer.scrollTo;
                    scrollContainer.scrollTo = () => { };

                    // Trigger the selection
                    flickityControls.flickityCarousel.select(`#${controlsId}`);

                    // Restore scrollTo after a short delay
                    setTimeout(() => {
                        scrollContainer.scrollTo = originalScrollTo;
                    }, 100);
                } else {
                    flickityControls.flickityCarousel.select(`#${controlsId}`);
                }
            }
        }
    }
});

/**
 * KIT + SERVICIO DE ENSAMBLAJE
 *
 * El kit y el "Servicio de Ensamblaje" se agregan al carrito como dos line items independientes
 * (ver snippets/product-form.liquid, addDefaultKitToCart), vinculados solo "a ojo" por texto:
 * el kit trae properties['Formato de entrega'] = 'Ensamblado y probado', y el servicio trae
 * properties['Para'] = <título del kit>. Shopify no vincula ambas líneas entre sí, así que si el
 * cliente elimina el servicio, la propiedad del kit se queda "pegada" en "Ensamblado y probado".
 *
 * La tienda usa además un cart drawer de una app externa (UpCart) que quita line items sin pasar
 * por el JS del tema, así que no podemos depender de eventos como "cart:updated" para detectarlo:
 * hacemos polling de /cart.js mientras haya un kit marcado como "Ensamblado y probado" en el carrito,
 * y lo apagamos en cuanto se resuelve, para no golpear el servidor de forma indefinida.
 */
let kitAssemblyPollTimer = null;

function stopKitAssemblyPolling() {
    if (kitAssemblyPollTimer) {
        clearInterval(kitAssemblyPollTimer);
        kitAssemblyPollTimer = null;
    }
}

function startKitAssemblyPolling() {
    if (kitAssemblyPollTimer) {
        return;
    }

    kitAssemblyPollTimer = setInterval(checkKitAssemblyState, 2000);
}

function reconcileKitAssemblyProperties(cart) {
    if (!cart || !Array.isArray(cart.items)) {
        return;
    }

    let hasPendingAssemblyKit = false;
    const fixes = [];

    cart.items.forEach((item, index) => {
        const assemblyType = item.properties && item.properties['Formato de entrega'];

        if (!assemblyType || assemblyType === 'Kit por armar') {
            return;
        }

        const hasMatchingService = cart.items.some((otherItem) => {
            return otherItem.properties && otherItem.properties['Para'] === item.product_title;
        });

        if (hasMatchingService) {
            hasPendingAssemblyKit = true;
            return;
        }

        fixes.push(
            fetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    line: index + 1,
                    quantity: item.quantity,
                    properties: Object.assign({}, item.properties, { 'Formato de entrega': 'Kit por armar' }),
                    sections: ['mini-cart']
                })
            }).then((response) => response.json())
        );
    });

    if (fixes.length > 0) {
        Promise.all(fixes)
            .then((updatedCarts) => {
                const updatedCart = updatedCarts[updatedCarts.length - 1];
                document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: updatedCart } }));
                document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: updatedCart, replacementDelay: 400 } }));

                // El drawer de UpCart mantiene su propio estado renderizado y no escucha los eventos
                // nativos del tema, así que forzamos que vuelva a pedir el carrito con su API pública.
                if (typeof window.upcartRefreshCart === 'function') {
                    window.upcartRefreshCart();
                } else if (typeof window.upcartOpenCart === 'function') {
                    window.upcartOpenCart();
                }
            })
            .catch((error) => console.error('Error al actualizar el tipo de ensamblaje del kit:', error));
    }

    if (hasPendingAssemblyKit) {
        startKitAssemblyPolling();
    } else {
        stopKitAssemblyPolling();
    }
}

function checkKitAssemblyState() {
    fetch('/cart.js', { credentials: 'same-origin' })
        .then((response) => response.json())
        .then((cart) => reconcileKitAssemblyProperties(cart))
        .catch(() => {});
}

// El tema despacha "cart:updated" tanto en "document" (product-form.liquid) como en
// "document.documentElement" (theme.js): reaccionamos al instante cuando el cambio viene de ahí.
document.addEventListener('cart:updated', (event) => reconcileKitAssemblyProperties(event.detail && event.detail.cart));
document.documentElement.addEventListener('cart:updated', (event) => reconcileKitAssemblyProperties(event.detail && event.detail.cart));

// Revisa el estado real del carrito al cargar la página, por si ya había un kit "Ensamblado y probado"
// pendiente de un cambio hecho fuera del tema (ej. el drawer de UpCart).
checkKitAssemblyState();